<template>
  <section>
    <h4 class="text-h4 mb-4">
      {{ t('shipment.containerArrivals') }}
    </h4>

    <VCard>
      <!-- Filters and Actions -->
      <VCardText>
        <div class="d-flex align-center flex-wrap gap-2 mb-4">
          <VBtn
            v-if="canCreateContainerArrival"
            color="primary"
            prepend-icon="tabler-plus"
            :to="{ name: 'shipment-container-arrivals-create' }"
          >
            {{ t('actions.create') }}
          </VBtn>
          
          <VSpacer />

          <VTextField
            v-model="search"
            :label="t('filters.search')"
            hide-details
            density="compact"
            prepend-icon="tabler-search"
            class="me-2"
            style="max-width: 250px;"
          />
        </div>

        <!-- Filter Section -->
        <VExpansionPanels
          v-model="expandedPanels"
          variant="accordion"
        >
          <VExpansionPanel>
            <VExpansionPanelTitle>
              {{ t('filters.advancedFilters') }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="d-flex flex-wrap gap-4 align-center">
                <!-- Status Filter -->
                <VSelect
                  v-model="filters.status"
                  :items="statusOptions"
                  :label="t('filters.status')"
                  density="compact"
                  hide-details
                  class="filter-select"
                  clearable
                  style="min-width: 220px;"
                />

                <!-- Transport Filter (Air | Sea | All) -->
                <VSelect
                  v-model="filters.transport_type"
                  :items="transportOptions"
                  :label="t('shipment.transport')"
                  density="compact"
                  hide-details
                  class="filter-select"
                  clearable
                  style="min-width: 180px;"
                />

                <!-- Date Preset Selector -->
                <VSelect
                  v-model="filters.date_preset"
                  :items="datePresets"
                  :label="t('filters.datePreset')"
                  density="compact"
                  hide-details
                  class="filter-select"
                  style="min-width: 160px;"
                />

                <!-- Custom Date Range (only shown when preset is 'custom') -->
                <div
                  v-if="filters.date_preset === 'custom'"
                  class="date-range-filter d-flex gap-2"
                  style="min-width: 360px;"
                >
                  <VTextField
                    v-model="filters.start_date"
                    :label="t('filters.startDate')"
                    type="date"
                    density="compact"
                    hide-details
                    class="filter-date"
                  />
                  <VTextField
                    v-model="filters.end_date"
                    :label="t('filters.endDate')"
                    type="date"
                    density="compact"
                    hide-details
                    class="filter-date"
                  />
                </div>

                <!-- Reset Filters Button -->
                <VBtn
                  color="secondary"
                  variant="outlined"
                  size="small"
                  prepend-icon="tabler-refresh"
                  @click="resetFilters"
                >
                  {{ t('filters.reset') }}
                </VBtn>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>

      <!-- Data Table -->
      <VDataTableServer
        v-model="selectedRows"
        v-model:sort-by="tableSort"
        :headers="headers"
        :items="containerArrivals"
        :loading="loading"
        :items-length="totalItems"
        item-value="id"
        return-object
        show-select
        @update:options="handleTableOptionsUpdate"
      >
        <!-- Custom rendering for ID column -->
        <template #item.id="{ item }">
          <div class="font-weight-medium">
            #{{ item.id }}
          </div>
        </template>
        
        <!-- Reference code (public): "1.4_SEA" — the short tag used
             everywhere for filters/URLs/group headers. Bold + primary
             so it reads as the row's identity. -->
        <template #item.reference_code="{ item }">
          <div class="font-weight-bold text-primary">
            {{ item.reference_code || '—' }}
          </div>
        </template>

        <!-- Arrival reference (public): "2.3.ARRIVAGE_MARS_2026_3eVague"
             — the human-readable batch label from the China-side Excel
             filename. Mirrors the team's tracking spreadsheet. -->
        <template #item.arrival_ref="{ item }">
          <span class="text-body-2">{{ item.arrival_ref || '—' }}</span>
        </template>

        <!-- Container number (superadmin only): "PCIU9336383" — the real
             shipping code used to query PIL/CMA for ETA. Backend redacts
             it for non-superadmin so item.container_number is null and
             we render an em-dash. -->
        <template #item.container_number="{ item }">
          <span v-if="item.container_number" class="text-body-2 font-mono">
            {{ item.container_number }}
          </span>
          <span v-else class="text-caption text-medium-emphasis">—</span>
        </template>

        <!-- Custom rendering for dates column -->
        <template #item.arrival_date="{ item }">
          <span>{{ item.arrival_date ? formatDate(item.arrival_date) : '-' }}</span>
        </template>
        
        <template #item.departure_date="{ item }">
          <span>{{ item.departure_date ? formatDate(item.departure_date) : '-' }}</span>
        </template>

        <!-- Transport chip: plane icon for air, ship icon for sea.
             Uses the existing shipment.by_plane / by_ship i18n keys. -->
        <template #item.transport_type="{ item }">
          <VChip
            v-if="item.transport_type === 'air'"
            size="small"
            color="info"
            variant="tonal"
            prepend-icon="tabler-plane"
          >
            {{ t('shipment.by_plane') }}
          </VChip>
          <VChip
            v-else-if="item.transport_type === 'sea'"
            size="small"
            color="primary"
            variant="tonal"
            prepend-icon="tabler-ship"
          >
            {{ t('shipment.by_ship') }}
          </VChip>
          <span v-else class="text-medium-emphasis text-caption">—</span>
        </template>

        <!-- Custom rendering for notes column -->
        <template #item.notes="{ item }">
          <div
            v-if="item.notes"
            class="notes-cell"
          >
            <VTooltip>
              <template #activator="{ props }">
                <div 
                  v-bind="props"
                  class="text-truncate" 
                  style="max-width: 200px;"
                >
                  {{ item.notes }}
                </div>
              </template>
              <span>{{ item.notes }}</span>
            </VTooltip>
          </div>
          <span
            v-else
            class="text-grey"
          >-</span>
        </template>

        <!-- Custom rendering for packing list column -->
        <template #item.packing_list="{ item }">
          <div v-if="item.packing_list">
            <VChip
              color="primary"
              size="small"
              label
              :to="`/shipment/packing-lists/${item.packing_list.id}/view`"
            >
              #{{ item.packing_list.id }}
            </VChip>
          </div>
          <span v-else>-</span>
        </template>

        <!-- Custom rendering for status column -->
        <template #item.status="{ item }">
          <VChip
            :color="getStatusColor(item.status)"
            size="small"
          >
            {{ t(`shipment.statuses.${item.status}`, item.status) }}
          </VChip>
        </template>

        <!-- Actions column with multiple buttons -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-1">
            <!-- Direct link to client-groups page for the container.
                 Saves the detour through Warehouse Operations → Select
                 Shipment → Arrange/Inspect when the user just wants to
                 see the per-customer breakdown for this container. -->
            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  icon
                  variant="text"
                  color="primary"
                  size="small"
                  v-bind="props"
                  :to="`/shipment/warehouse/client-groups/${item.id}`"
                >
                  <VIcon>tabler-users-group</VIcon>
                </VBtn>
              </template>
              {{ t('shipment.client_groups') }}
            </VTooltip>

            <!-- Container-wide cotation (replaces the Excel TEST COTATION). -->
            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  icon
                  variant="text"
                  color="success"
                  size="small"
                  v-bind="props"
                  :to="`/shipment/warehouse/cotation/${item.id}`"
                >
                  <VIcon>tabler-calculator</VIcon>
                </VBtn>
              </template>
              Cotation
            </VTooltip>

            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  v-if="canEditContainerArrival"
                  icon
                  variant="text"
                  color="warning"
                  size="small"
                  v-bind="props"
                  :to="`/shipment/container-arrivals/${item.id}/edit`"
                >
                  <VIcon>tabler-edit</VIcon>
                </VBtn>
              </template>
              {{ t('actions.edit') }}
            </VTooltip>

            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  v-if="canEditContainerArrival"
                  icon
                  variant="text"
                  color="info"
                  size="small"
                  v-bind="props"
                  @click="openStatusUpdateModal(item)"
                >
                  <VIcon>tabler-refresh</VIcon>
                </VBtn>
              </template>
              {{ t('shipment.update_status') }}
            </VTooltip>

            <VTooltip v-if="item.status === 'empty' && !item.packing_list">
              <template #activator="{ props }">
                <VBtn
                  v-if="canDeleteContainerArrival"
                  icon
                  variant="text"
                  color="primary"
                  size="small"
                  v-bind="props"
                  :to="`/shipment/packing-lists/create?container_id=${item.id}`"
                >
                  <VIcon>tabler-list-check</VIcon>
                </VBtn>
              </template>
              {{ t('shipment.createPackingList') }}
            </VTooltip>

            <VTooltip>
              <template #activator="{ props }">
                <VBtn
                  v-if="canDeleteContainerArrival"
                  icon
                  variant="text"
                  color="error"
                  size="small"
                  :disabled="!isSuperadmin"
                  v-bind="props"
                  @click="confirmDelete(item)"
                >
                  <VIcon>tabler-trash</VIcon>
                </VBtn>
              </template>
              {{ t('actions.delete') }}
            </VTooltip>
          </div>
        </template>

      </VDataTableServer>
      
      <!-- Batch Actions -->
      <VCardText v-if="selectedRows.length > 0">
        <div class="d-flex justify-space-between align-center">
          <div class="d-flex gap-2">
            <VBtn
              v-if="canDeleteContainerArrival"
              v-tooltip="!isSuperadmin ? t('shipment.superadmin_only') : ''"
              color="error"
              prepend-icon="tabler-trash"
              size="small"
              :disabled="!isSuperadmin"
              @click="confirmMassDelete"
            >
              {{ t('actions.deleteSelected') }}
            </VBtn>
          </div>

          <div>
            {{ t('shipment.selectedItems', { count: selectedRows.length }) }}
            <span v-if="selectedRows.length > 0" class="text-caption text-grey ms-2">
              ({{ selectedRows.map(row => `#${row.id}`).join(', ') }})
            </span>
          </div>
        </div>
      </VCardText>

      <!-- Confirm Delete Dialog -->
      <VDialog
        v-model="deleteDialog.open"
        max-width="500"
      >
        <VCard>
          <VCardTitle class="text-h5">
            {{ t('messages.confirmDeleteTitle') }}
          </VCardTitle>
          <VCardText>
            {{ t('messages.confirmDeleteContainerArrival') }}
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="secondary"
              variant="text"
              @click="deleteDialog.open = false"
            >
              {{ t('actions.cancel') }}
            </VBtn>
            <VBtn
              color="error"
              :loading="deleteDialog.loading"
              @click="deleteSingleContainerArrival"
            >
              {{ t('actions.delete') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- Mass Delete Confirmation Dialog -->
      <VDialog
        v-model="showConfirmDeleteDialog"
        max-width="500"
      >
        <VCard>
          <VCardTitle class="text-h5">
            {{ t('messages.confirmDeleteTitle') }}
          </VCardTitle>
          <VCardText>
            {{ t('messages.confirmDeleteMultiple', { count: selectedRows.length }) }}
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="secondary"
              variant="text"
              @click="showConfirmDeleteDialog = false"
            >
              {{ t('actions.cancel') }}
            </VBtn>
            <VBtn
              color="error"
              @click="handleDeleteSelected"
            >
              {{ t('actions.delete') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </VCard>
    <ContainerStatusUpdateModal
      v-model="showStatusUpdateModal"
      :container-id="selectedContainer?.id"
      :current-status="selectedContainer?.status"
      @status-updated="handleStatusUpdated"
    />
  </section>
</template>

<script setup>
definePage({
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Parcels',
  },
})

import { ref, onMounted, computed, watch, reactive, nextTick } from 'vue'
import { useContainerArrivalsManagement } from '@/composables/useContainerArrivalsManagement'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useSnackbar } from '@/composables/useSnackbar'
import { useSuperadmin } from '@/composables/useSuperadmin'
import { useAbility } from '@/plugins/casl/composables/useAbility'
import ContainerStatusUpdateModal from '@/components/shipment/ContainerStatusUpdateModal.vue'

const { t } = useI18n()
const router = useRouter()
const { success, error } = useSnackbar()
const { isSuperadmin } = useSuperadmin()

const { 
  containerArrivals, 
  containerArrivalsMeta,
  fetchContainerArrivals, 
  deleteContainerArrival, 
  deleteMultipleContainerArrivals,
  updateContainerStatus, 
  loading,
} = useContainerArrivalsManagement()

const { can } = useAbility()

const canEditContainerArrival = computed(() => can('edit', 'Containerarrival'))
// Hard-lock delete to superadmin (matches RestrictDeletesToSuperadmin
// middleware). Hide rather than disable so admins don't see a teaser.
const canDeleteContainerArrival = computed(() => isSuperadmin.value)
const canReadContainerArrival = computed(() => can('read', 'Containerarrival'))
const canCreateContainerArrival = computed(() => can('create', 'Containerarrival'))

// Pagination variables
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const totalPages = ref(1)

const selectedRows = ref([])
const search = ref('')
const showConfirmDeleteDialog = ref(false)
const expandedPanels = ref([0])

// Delete Dialog State
const deleteDialog = ref({
  open: false,
  loading: false,
  item: null,
})

// Status Dialog State
const statusDialog = ref({
  open: false,
  loading: false,
  item: null,
  newStatus: '',
})

// Get current year dates
const getCurrentYearDates = () => {
  const now = new Date()
  const firstDay = new Date(now.getFullYear(), 0, 1) // Jan 1
  const lastDay = new Date(now.getFullYear(), 11, 31) // Dec 31

  // Format as YYYY-MM-DD (internal formatting for API parameters)
  const formatDateYMD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  return {
    start: formatDateYMD(firstDay),
    end: formatDateYMD(lastDay),
  }
}

// Get current year dates
const currentYear = getCurrentYearDates()

// Filter values with defaults
const filters = reactive({
  status: '',
  // Transport-type filter (sea/air). Useful for splitting the list
  // by physical mode — sea containers vs the new air arrivals
  // (5.18_AIR-style). Empty = both.
  transport_type: '',
  start_date: currentYear.start,
  end_date: currentYear.end,
  date_preset: 'this_year',
})

// Transport options for the filter dropdown.
const transportOptions = computed(() => [
  { title: t('filters.all'), value: '' },
  { title: t('shipment.by_plane'), value: 'air' },
  { title: t('shipment.by_ship'), value: 'sea' },
])

// Helper function to format date according to locale
const formatDate = dateString => {
  if (!dateString) return '-'

  try {
    const date = new Date(dateString)

    // Get current locale from i18n
    const { locale } = useI18n()
    const currentLocale = locale.value || 'en'

    // Use Intl.DateTimeFormat with the current i18n locale for consistent formatting
    return new Intl.DateTimeFormat(currentLocale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date)
  } catch (e) {
    // Return the original string if there's an error
    return dateString
  }
}

// Status options for filtering — only the 3 statuses actually used
// in the DB (verified via tinker 2026-05-17): empty/shipped/arrived.
// The legacy 'loaded', 'in_port', 'unloaded' options never had
// matching rows so they filtered to empty results, only confused
// operators with untranslated keys.
const statusOptions = computed(() => [
  { title: t('filters.all'), value: '' },
  { title: t('shipment.statuses.empty'), value: 'empty' },
  { title: t('shipment.statuses.shipped'), value: 'shipped' },
  { title: t('shipment.statuses.arrived'), value: 'arrived' },
])

// Date preset options
const datePresets = computed(() => [
  { title: t('filters.today'), value: 'today' },
  { title: t('filters.yesterday'), value: 'yesterday' },
  { title: t('filters.thisWeek'), value: 'this_week' },
  { title: t('filters.lastWeek'), value: 'last_week' },
  { title: t('filters.thisMonth'), value: 'current_month' },
  { title: t('filters.lastMonth'), value: 'last_month' },
  { title: t('filters.thisYear'), value: 'this_year' },
  { title: t('filters.lastYear'), value: 'last_year' },
  { title: t('filters.custom'), value: 'custom' },
])

const showStatusUpdateModal = ref(false)
const selectedContainer = ref(null)

const openStatusUpdateModal = container => {
  selectedContainer.value = container
  showStatusUpdateModal.value = true
}

const handleStatusUpdated = () => {
  fetchContainerArrivals()
}

// Apply filters
const applyFilters = (clearSelection = true) => {
  // Create params object
  const params = {}

  // Only add defined filters to avoid sending undefined values
  if (filters.status && filters.status !== '' && filters.status !== 'all') {
    params.status = filters.status
  }

  // Transport type filter — backend index already supports it.
  if (filters.transport_type && filters.transport_type !== '') {
    params.transport_type = filters.transport_type
  }

  if (filters.start_date) {
    params.start_date = filters.start_date
  }

  if (filters.end_date) {
    params.end_date = filters.end_date
  }

  if (search.value) {
    params.search = search.value
  }

  // Add pagination parameters
  params.page = currentPage.value
  params.per_page = itemsPerPage.value

  // Pass-through table-driven sort (only when the user clicked a header;
  // otherwise the backend's GREATEST default kicks in).
  if (sortBy.value) {
    params.sort_by = sortBy.value
    params.sort_order = sortOrder.value
  }

  // Apply filters and fetch container arrivals
  fetchContainerArrivals(params).then(data => {
    // Container arrivals fetched successfully
    // Update pagination metadata
    totalItems.value = containerArrivalsMeta.value?.total || 0
    totalPages.value = containerArrivalsMeta.value?.last_page || 1
    
    // Only clear selections when explicitly requested (like after filtering)
    if (clearSelection) {
      selectedRows.value = []
    }
  }).catch(err => {
    error(t('messages.fetchError', 'Error fetching container arrivals'))
  })
}

// Handle date preset selection
const handleDatePresetChange = preset => {
  const now = new Date()
  let start, end

  switch(preset) {
  case 'today':
    start = end = new Date(now)
    break

  case 'yesterday':
    start = end = new Date(now)
    start.setDate(start.getDate() - 1)
    end.setDate(end.getDate() - 1)
    break

  case 'this_week':
    start = new Date(now)
    start.setDate(now.getDate() - now.getDay()) // Start of week (Sunday)
    end = new Date(now)
    break

  case 'last_week':
    start = new Date(now)
    start.setDate(now.getDate() - now.getDay() - 7) // Start of last week
    end = new Date(start)
    end.setDate(start.getDate() + 6) // End of last week
    break

  case 'current_month':
    start = new Date(now.getFullYear(), now.getMonth(), 1)
    end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    break

  case 'last_month':
    start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    end = new Date(now.getFullYear(), now.getMonth(), 0)
    break

  case 'this_year':
    start = new Date(now.getFullYear(), 0, 1)  // January 1
    end = new Date(now.getFullYear(), 11, 31) // December 31
    break

  case 'last_year':
    start = new Date(now.getFullYear() - 1, 0, 1)  // January 1 last year
    end = new Date(now.getFullYear() - 1, 11, 31) // December 31 last year
    break
  case 'custom':
    // Don't set dates automatically, let user pick them
    return
  }

  // Format dates
  const formatDate = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  // Set date values
  filters.start_date = formatDate(start)
  filters.end_date = formatDate(end)

  // Apply filters
  applyFilters()
}

// Watch date preset changes
watch(() => filters.date_preset, newValue => {
  if (newValue) {
    handleDatePresetChange(newValue)
  }
})

// Reset filters
const resetFilters = () => {
  // Reset to default values
  filters.status = ''
  filters.transport_type = ''
  filters.date_preset = 'this_year'

  // Current year dates will be set by the watcher
  search.value = ''
  applyFilters()
}

// Watch for specific filter changes (avoid conflicts with date preset watcher)
watch(() => filters.status, () => {
  applyFilters()
})

watch(() => filters.transport_type, () => {
  applyFilters()
})

watch(search, () => {
  applyFilters()
})

// Watch for custom date changes (only when preset is custom)
watch([() => filters.start_date, () => filters.end_date], () => {
  if (filters.date_preset === 'custom') {
    applyFilters()
  }
})

// Duplicate-fetch guard, see incoming-parcels/index.vue.
const pageReady = ref(false)

// Server-side sort state. Empty by default so the backend falls through to
// its composite GREATEST(arrival, departure, created) DESC. Once the user
// clicks a header, both refs populate and override the default.
const sortBy = ref(null)
const sortOrder = ref('desc')
// Two-way bound to VDataTableServer's sort-by — keeps the visible header
// arrow in sync with what we actually request from the API.
const tableSort = ref([])

// Handle VDataTableServer options update
const handleTableOptionsUpdate = options => {
  if (!pageReady.value) return
  if (options.page !== currentPage.value) {
    currentPage.value = options.page
  }
  if (options.itemsPerPage !== itemsPerPage.value) {
    itemsPerPage.value = options.itemsPerPage
    currentPage.value = 1
  }
  if (options.sortBy && options.sortBy.length > 0) {
    sortBy.value = options.sortBy[0].key
    sortOrder.value = options.sortBy[0].order || 'asc'
  } else {
    // User cleared the sort — fall back to backend's GREATEST default.
    sortBy.value = null
    sortOrder.value = 'desc'
  }
  applyFilters(false) // Don't clear selections when just changing pagination
}

// Function to handle page changes
const onPageChange = page => {
  currentPage.value = page
  applyFilters(false) // Don't clear selections when just changing pages
}

// Function to handle items per page changes
const onItemsPerPageChange = perPage => {
  itemsPerPage.value = perPage
  currentPage.value = 1 // Reset to first page when changing items per page
  applyFilters()
}

// Lifecycle hooks
onMounted(async () => {
  // Apply default filters
  await applyFilters()
  await nextTick()
  pageReady.value = true
})

// Computed Headers
// Column sorting is wired through to the backend (allowlisted columns:
// id, container_number, departure_date, arrival_date, created_at). The
// default order is the composite GREATEST(arrival, departure, created)
// DESC — applied by the backend whenever the request has no sort_by.
// Once a user clicks a header, sortBy/sortOrder are sent and override
// the composite default. Status + Notes + Packing List columns can't be
// sorted server-side so they stay sortable:false.
const headers = computed(() => {
  const cols = [
    { title: t('tableHeaders.id'), key: 'id', align: 'start' },
    // Public — short tag everyone uses to identify the batch.
    { title: t('shipment.reference_code'), key: 'reference_code', align: 'start' },
    // Public — human-readable arrival label from Excel filename.
    { title: t('shipment.arrival_ref'), key: 'arrival_ref', align: 'start', sortable: false },
    { title: t('tableHeaders.departureDate'), key: 'departure_date', align: 'start' },
    { title: t('tableHeaders.arrivalDate'), key: 'arrival_date', align: 'start' },
    // Air vs Sea badge. Air arrivals are the new partner-shipment
    // flow; sea is the legacy container flow. Same table, mixed rows.
    { title: t('shipment.transport'), key: 'transport_type', align: 'center', sortable: false },
  ]

  // Container number (PCIU9336383 etc) is superadmin-only — used for
  // PIL/CMA ETA lookups, sensitive carrier-side data. Backend redacts
  // for non-superadmins so the column would render em-dashes for
  // everyone. Hide the column entirely when the user can't see any
  // value in it.
  if (isSuperadmin.value) {
    cols.push({
      title: t('shipment.container_number'),
      key: 'container_number',
      align: 'start',
      sortable: false,
    })
  }

  cols.push(
    { title: t('shipment.notes'), key: 'notes', align: 'start', sortable: false },
    { title: t('tableHeaders.packingList'), key: 'packing_list', align: 'start', sortable: false },
    { title: t('tableHeaders.status'), key: 'status', align: 'center', sortable: false },
    { title: t('tableHeaders.actions'), key: 'actions', align: 'center', sortable: false },
  )

  return cols
})

// Helper function to get status color
const getStatusColor = status => {
  switch (status) {
  case 'empty':
    return 'primary'
  case 'loaded':
    return 'info'
  case 'shipped':
    return 'warning'
  case 'in_port':
    return 'success'
  case 'unloaded':
    return 'grey'
  default:
    return 'grey'
  }
}

// Open status change dialog
const openStatusDialog = item => {
  statusDialog.value.item = item
  statusDialog.value.newStatus = item.status
  statusDialog.value.open = true
}

// Update container status
const updateStatus = async () => {
  if (!statusDialog.value.item || !statusDialog.value.newStatus) return

  statusDialog.value.loading = true
  try {
    await updateContainerStatus(statusDialog.value.item.id, statusDialog.value.newStatus)

    // Refresh the container arrivals without clearing selections
    applyFilters(false)

    // Show success message
    success(t('shipment.status_updated', 'Container status updated successfully'))
  } catch (err) {
    console.error('Update status error:', err)
    error(t('messages.updateError', 'Failed to update container status'))
  } finally {
    statusDialog.value.loading = false
    statusDialog.value.open = false
    statusDialog.value.item = null
    statusDialog.value.newStatus = ''
  }
}

// Confirm delete container arrival
const confirmDelete = item => {
  deleteDialog.value.item = item
  deleteDialog.value.open = true
}

// Delete container arrival
const deleteSingleContainerArrival = async () => {
  if (!deleteDialog.value.item) return

  deleteDialog.value.loading = true
  try {
    await deleteContainerArrival(deleteDialog.value.item.id)

    // Refresh the container arrivals without clearing selections
    applyFilters(false)

    // Show success message
    success(t('shipment.container_deleted', 'Container arrival deleted successfully'))
  } catch (err) {
    console.error('Delete error:', err)
    error(t('messages.deleteError', 'Failed to delete container arrival'))
  } finally {
    deleteDialog.value.loading = false
    deleteDialog.value.open = false
    deleteDialog.value.item = null
  }
}

// Confirm delete selected rows
const confirmMassDelete = () => {
  if (!selectedRows.value.length) return
  showConfirmDeleteDialog.value = true
}

// Delete Selected Rows
const handleDeleteSelected = async () => {
  if (!selectedRows.value.length) return

  try {
    await deleteMultipleContainerArrivals(selectedRows.value)

    showConfirmDeleteDialog.value = false
    selectedRows.value = []

    // Refresh the data to reflect deleted items (clear selections since items were deleted)
    await applyFilters(true)

    // Show success message
    success(t('shipment.containers_deleted', 'Container arrivals deleted successfully'))
  } catch (err) {
    console.error('Error in handleDeleteSelected:', err)
    error(t('messages.deleteError', 'Error deleting container arrivals'))
  }
}
</script>

<style scoped>
.v-table {
  width: 100%;
}
</style>
